import React, { ReactNode, useMemo } from 'react';
import { IPickerOption, IPickerProps } from '../../types';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import './style.scss';

type TPickerOption<T> = Pick<IPickerProps<T>, 'disabled' | 'viewedSize' | 'hideSelectedIcons' | 'optionClassName'>;

interface IProps<T> extends TPickerOption<T> {
  item?: IPickerOption;
  isSelected?: boolean;
  unavailable?: string | null;
  onClick?: (item: IPickerOption) => void;
  children?: ReactNode;
}

const PickerOption = <T,>(props: IProps<T>): JSX.Element => {
  const onClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const target: HTMLLIElement = event.target as HTMLLIElement;

    if (
      !props.item ||
      !props.onClick ||
      target?.classList.contains('icons-icon') ||
      target?.classList.contains('tooltip__overlay') ||
      props.disabled
    ) {
      return;
    }

    props.onClick(props.item);
  };

  const optionClassName = useMemo((): string => {
    let className: string = 'picker-option';

    if (props.optionClassName) className += ` ${props.optionClassName}`;
    if (props.viewedSize === 'small') className += ' picker-option_small';
    if (props.hideSelectedIcons && props.isSelected) className += ' picker-option_selected';
    if (props.disabled) className += ' disabled';

    return className;
  }, [props.optionClassName, props.viewedSize, props.disabled, props.isSelected, props.hideSelectedIcons]);

  const markText = useMemo((): string | null => {
    return props.unavailable || null;
  }, [props.unavailable]);

  return (
    <li className={optionClassName} onClick={onClick}>
      {props.children ? (
        props.children
      ) : (
        <>
          {props.hideSelectedIcons ? null : (
            <Icon
              name={Icons.Check}
              size={props.viewedSize === 'small' ? 18 : 22}
              color={theme.colors.mainblue}
              className={`mr-3 ${!props.isSelected ? 'picker-option_unselected' : ''}`}
            />
          )}
          <p className="picker-option__text text-elipsis">{props.item?.label || ''}</p>
          {markText ? (
            <div className="picker-option__mark ml-2">
              <CustomTooltip message={markText} position={TOOLTIP_POSITION.PORTAL} duration={TOOLTIP_DURATION.EXTRA_LARGE}>
                <Icon name={Icons.Warning} size={15} color={theme.colors.red} />
              </CustomTooltip>
            </div>
          ) : null}
        </>
      )}
    </li>
  );
};

export default PickerOption;
